/**
 * SalesDashboard — KPIs, Funnel, Trend-Charts, Hot Leads.
 *
 * @page /sales/dashboard
 * @roles admin, manager
 */
import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Info, Flame, ArrowRight, Users, Trophy, TrendingUp, Euro } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  Sankey, Rectangle, Layer,
} from 'recharts';

const API = import.meta.env.VITE_API_URL || '';

function api(path) {
  return fetch(`${API}${path}`, { credentials: 'include' }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data;
  });
}

const PERIODS = [
  { key: 'week', labelKey: 'sales.dashboard.thisWeek' },
  { key: 'month', labelKey: 'sales.dashboard.thisMonth' },
  { key: '3months', labelKey: 'sales.dashboard.last3Months' },
  { key: 'all', labelKey: 'sales.dashboard.allTime' },
];

const STAGE_LABELS = {
  preisanfrage: 'sales.stages.preisanfrage',
  neu: 'sales.stages.neu',
  kontaktiert: 'sales.stages.kontaktiert',
  termin_vereinbart: 'sales.stages.termin_vereinbart',
  angebot_gesendet: 'sales.stages.angebot_gesendet',
  gewonnen: 'sales.stages.gewonnen',
  verloren: 'sales.stages.verloren',
  kalt: 'sales.stages.kalt',
};

const SEGMENT_COLORS = {
  startup: '#E5E7EB',
  midscale: '#6366F1',
  enterprise: '#338edc',
};

const SEGMENT_LABELS = {
  startup: 'Start-Up',
  midscale: 'Mid-Scale',
  enterprise: 'Enterprise',
};

function KpiCard({ label, value, sub, info, icon: Icon, iconColor }) {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div className="border border-[var(--border-card)] rounded-2xl bg-[var(--bg-card)] p-4 relative">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[11px] font-medium text-[var(--text-secondary)] uppercase tracking-wide">{label}</span>
            {info && (
              <button
                onMouseEnter={() => setShowInfo(true)}
                onMouseLeave={() => setShowInfo(false)}
                className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
              >
                <Info className="w-3.5 h-3.5" />
              </button>
            )}
            {showInfo && info && (
              <div className="absolute top-12 left-4 right-4 z-10 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[8px] p-3 text-[11px] text-[var(--text-secondary)] shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
                {info}
              </div>
            )}
          </div>
          <div className="text-[22px] font-semibold text-[var(--text-primary)]">{value}</div>
          {sub && <div className="text-[11px] text-[var(--text-tertiary)] mt-0.5">{sub}</div>}
        </div>
        {Icon && <Icon size={20} className={iconColor || "text-[var(--text-tertiary)]"} />}
      </div>
    </div>
  );
}

const STAGE_COLORS = {
  preisanfrage: '#22D3EE',
  neu: '#338edc',
  kontaktiert: '#6366F1',
  termin_vereinbart: '#0891B2',
  angebot_gesendet: '#4D9CE0',
  gewonnen: '#16A34A',
  verloren: '#9CA3AF',
  kalt: '#D1D5DB',
};

function SankeyNode({ x, y, width, height, index, payload }) {
  const stage = payload?.name;
  const color = STAGE_COLORS[stage] || '#338edc';
  return (
    <Layer>
      <Rectangle x={x} y={y} width={width} height={height} fill={color} radius={[4, 4, 4, 4]} />
      {width != null && (
        <text x={x + width + 6} y={y + height / 2} textAnchor="start" dominantBaseline="central" style={{ fontSize: 11, fill: '#6B7280' }}>
          {stage}
        </text>
      )}
    </Layer>
  );
}

function SankeyLink({ sourceX, sourceY, sourceControlX, targetX, targetY, targetControlX, linkWidth, payload }) {
  const stage = payload?.source?.name;
  const color = STAGE_COLORS[stage] || '#338edc';
  return (
    <Layer>
      <path
        d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
        fill="none"
        stroke={color}
        strokeWidth={linkWidth}
        strokeOpacity={0.3}
      />
    </Layer>
  );
}

function FunnelBars({ funnel, t }) {
  if (!funnel || funnel.length === 0) return null;
  const maxAnzahl = Math.max(...funnel.map(d => parseInt(d.anzahl) || 0), 1);
  return (
    <div className="space-y-2">
      {funnel.map((item) => {
        const anzahl = parseInt(item.anzahl) || 0;
        const pct = (anzahl / maxAnzahl) * 100;
        return (
          <div key={item.stage} className="flex items-center gap-3">
            <span className="text-[12px] text-[var(--text-secondary)] w-[120px] truncate">
              {t(STAGE_LABELS[item.stage]) || item.stage}
            </span>
            <div className="flex-1 h-6 bg-[var(--bg-inset)] rounded-[4px] overflow-hidden">
              <div
                className="h-full rounded-[4px] transition-all duration-500"
                style={{
                  width: `${Math.max(pct, 2)}%`,
                  backgroundColor: STAGE_COLORS[item.stage] || '#338edc',
                }}
              />
            </div>
            <span className="text-[13px] font-medium text-[var(--text-primary)] w-10 text-right">{anzahl}</span>
          </div>
        );
      })}
    </div>
  );
}

function SankeyChart({ data, t }) {
  if (!data) return <div className="text-[13px] text-[var(--text-tertiary)]">{t('common.noData') || 'Keine Daten'}</div>;

  const hasSankey = data.links && data.links.length > 0;

  return (
    <div>
      {hasSankey ? (
        <Sankey
          width={480}
          height={260}
          data={{ nodes: data.nodes, links: data.links }}
          nodeWidth={10}
          nodePadding={14}
          linkCurvature={0.5}
          node={<SankeyNode />}
          link={<SankeyLink />}
        >
          <Tooltip
            content={({ payload }) => {
              if (!payload || !payload.length) return null;
              const p = payload[0]?.payload;
              if (!p) return null;
              if (p.source && p.target) {
                return (
                  <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-lg p-2 text-[12px] shadow-sm">
                    <span style={{ color: 'var(--text-primary)' }}>
                      {t(STAGE_LABELS[p.source.name]) || p.source.name} → {t(STAGE_LABELS[p.target.name]) || p.target.name}
                    </span>
                    <span className="ml-2 font-semibold" style={{ color: 'var(--text-primary)' }}>{p.value}</span>
                  </div>
                );
              }
              return (
                <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-lg p-2 text-[12px] shadow-sm">
                  <span style={{ color: 'var(--text-primary)' }}>
                    {t(STAGE_LABELS[p.name]) || p.name}: {p.value}
                  </span>
                </div>
              );
            }}
          />
        </Sankey>
      ) : (
        <FunnelBars funnel={data.funnel} t={t} />
      )}

      {/* Conversion Summary */}
      {data.conversion && (
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[var(--border-card)]">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: '#16A34A' }} />
            <span className="text-[11px] text-[var(--text-secondary)]">{t('sales.dashboard.wonLeads')}</span>
            <span className="text-[12px] font-semibold text-[var(--text-primary)]">{data.conversion.gewonnen}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: '#9CA3AF' }} />
            <span className="text-[11px] text-[var(--text-secondary)]">{t('sales.dashboard.lost') || 'Lost'}</span>
            <span className="text-[12px] font-semibold text-[var(--text-primary)]">{data.conversion.verloren}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: '#338edc' }} />
            <span className="text-[11px] text-[var(--text-secondary)]">{t('sales.dashboard.inPipeline') || 'In Pipeline'}</span>
            <span className="text-[12px] font-semibold text-[var(--text-primary)]">{data.conversion.in_pipeline}</span>
          </div>
          <div className="ml-auto text-[12px] font-semibold text-[var(--text-primary)]">
            {data.conversion.rate}% {t('sales.dashboard.conversionRate')}
          </div>
        </div>
      )}
    </div>
  );
}

function SegmentDonut({ segmente }) {
  if (!segmente || segmente.length === 0) return null;

  const data = segmente.map(s => ({
    name: SEGMENT_LABELS[s.segment] || s.segment,
    value: parseInt(s.anzahl) || 0,
    color: SEGMENT_COLORS[s.segment] || '#E5E7EB',
  }));

  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className="flex items-center gap-4">
      <ResponsiveContainer width={160} height={160}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={45}
            outerRadius={70}
            dataKey="value"
            stroke="none"
          >
            {data.map((d, i) => (
              <Cell key={i} fill={d.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="space-y-2">
        {data.map((d) => (
          <div key={d.name} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
            <span className="text-[12px] text-[var(--text-secondary)]">{d.name}</span>
            <span className="text-[12px] font-medium text-[var(--text-primary)]">
              {total > 0 ? Math.round((d.value / total) * 100) : 0}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrendChart({ mode, setMode, t }) {
  const [data, setData] = useState([]);

  useEffect(() => {
    api(`/api/sales/dashboard/trend?mode=${mode}`).then(setData).catch(console.error);
  }, [mode]);

  const chartData = data.map(d => ({
    periode: new Date(d.periode).toLocaleDateString('de-DE', mode === 'month'
      ? { month: 'short', year: '2-digit' }
      : { day: '2-digit', month: '2-digit' }),
    neu: parseInt(d.neu) || 0,
    gewonnen: parseInt(d.gewonnen) || 0,
  }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">
          {t('sales.dashboard.trend') || 'Leads pro Woche'}
        </h3>
        <div className="flex gap-1 bg-[var(--bg-inset)] rounded-[8px] p-0.5">
          {['week', 'month'].map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-1 rounded-[6px] text-[11px] font-medium transition-all ${
                mode === m ? 'bg-white dark:bg-gray-700 text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-secondary)]'
              }`}
            >
              {m === 'week' ? 'Woche' : 'Monat'}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} barGap={2}>
          <XAxis dataKey="periode" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border-card)',
              borderRadius: 8,
              fontSize: 12,
            }}
          />
          <Bar dataKey="neu" name={t('sales.dashboard.newLeads') || 'Neue Leads'} fill="#338edc" radius={[4, 4, 0, 0]} barSize={20} />
          <Bar dataKey="gewonnen" name={t('sales.dashboard.wonLeadsChart') || 'Gewonnen'} fill="#6366F1" radius={[4, 4, 0, 0]} barSize={20} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function SalesDashboard() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('all');
  const [kpis, setKpis] = useState(null);
  const [sankey, setSankey] = useState(null);
  const [trendMode, setTrendMode] = useState('week');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [kpiData, sankeyData] = await Promise.all([
        api(`/api/sales/dashboard?period=${period}`),
        api(`/api/sales/dashboard/sankey?period=${period}`),
      ]);
      setKpis(kpiData);
      setSankey(sankeyData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading || !kpis) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin h-5 w-5 border-2 border-[var(--primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  const formatEur = (v) => {
    if (v >= 1000) return `€${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
    return `€${v}`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-[22px] font-semibold text-[var(--text-primary)]">
          {t('sales.dashboard.title') || 'Sales Dashboard'}
        </h1>
        <div className="flex gap-1 bg-[var(--bg-inset)] rounded-2xl p-1">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-all ${
                period === p.key ? 'bg-[var(--primary-light)] text-[#338edc] font-semibold' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              {t(p.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Row 1: KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          label={t('sales.dashboard.activeLeads') || 'Aktive Leads'}
          value={parseInt(kpis.leads_aktiv) || 0}
          sub={`+${parseInt(kpis.leads_neu_7d) || 0} ${t('sales.dashboard.thisWeek') || 'diese Woche'}`}
          icon={Users}
          iconColor="text-[#338edc]"
        />
        <KpiCard
          label={t('sales.dashboard.wonLeads') || 'Gewonnen'}
          value={parseInt(kpis.leads_gewonnen) || 0}
          sub={kpis.avg_deal_tage
            ? `${t('sales.dashboard.avgDealDays') || 'Ø'} ${kpis.avg_deal_tage} ${t('sales.dashboard.days') || 'Tage'}`
            : `${parseInt(kpis.gewonnen_30d) || 0} / 30${t('sales.dashboard.days') || 'T'}`
          }
          icon={Trophy}
          iconColor="text-[#F59E0B]"
        />
        <KpiCard
          label={t('sales.dashboard.conversionRate') || 'Conversion Rate'}
          value={`${parseFloat(kpis.conversion_rate) || 0}%`}
          sub={`${parseInt(kpis.leads_gewonnen) || 0} / ${parseInt(kpis.leads_gesamt) || 0} Leads`}
          icon={TrendingUp}
          iconColor="text-[#10B981]"
        />
        <KpiCard
          label={t('sales.dashboard.pipelineValue') || 'Pipeline-Wert'}
          value={formatEur(kpis.pipeline_wert_eur || 0)}
          sub={t('sales.dashboard.estimated') || 'geschätzt'}
          info={t('sales.dashboard.pipelineValueHint') || 'Geschätzter monatlicher Umsatz bei Abschluss aller aktiven Leads. Basis: Start-Up €500/M, Mid-Scale €3.000/M, Enterprise €15.000/M'}
          icon={Euro}
          iconColor="text-[#6366F1]"
        />
      </div>

      {/* Row 2: Funnel + Segment */}
      <div className="grid grid-cols-2 gap-4">
        <div className="border border-[var(--border-card)] rounded-2xl bg-[var(--bg-card)] p-5">
          <h3 className="text-[14px] font-semibold text-[var(--text-primary)] mb-4">
            {t('sales.dashboard.leadFlow') || 'Lead Flow'}
          </h3>
          <SankeyChart data={sankey} t={t} />
        </div>
        <div className="border border-[var(--border-card)] rounded-2xl bg-[var(--bg-card)] p-5">
          <h3 className="text-[14px] font-semibold text-[var(--text-primary)] mb-4">
            {t('sales.dashboard.bySegment') || 'Nach Segment'}
          </h3>
          <SegmentDonut segmente={kpis.segmente} />
        </div>
      </div>

      {/* Row 3: Trend Chart */}
      <div className="border border-[var(--border-card)] rounded-2xl bg-[var(--bg-card)] p-5">
        <TrendChart mode={trendMode} setMode={setTrendMode} t={t} />
      </div>

      {/* Row 4: Hot Leads + Onboarding */}
      <div className="grid grid-cols-2 gap-4">
        {/* Hot Leads */}
        <div className="border border-[var(--border-card)] rounded-2xl bg-[var(--bg-card)] p-5">
          <h3 className="text-[14px] font-semibold text-[var(--text-primary)] mb-3">
            {t('sales.dashboard.hotLeads') || 'Heiße Leads'}
          </h3>
          {kpis.hot_leads && kpis.hot_leads.length > 0 ? (
            <div className="space-y-2">
              {kpis.hot_leads.map(lead => (
                <div
                  key={lead.id}
                  className="flex items-center justify-between py-2 px-3 rounded-[8px] hover:bg-[var(--bg-card-hover)] cursor-pointer"
                  onClick={() => navigate('/sales/anfragen')}
                >
                  <div className="flex items-center gap-2">
                    <Flame className="w-4 h-4 text-orange-500" />
                    <div>
                      <div className="text-[13px] font-medium text-[var(--text-primary)]">
                        {lead.firma || lead.ansprechpartner}
                      </div>
                      <div className="text-[11px] text-[var(--text-tertiary)]">{lead.segment}</div>
                    </div>
                  </div>
                  <span className="text-[15px] font-semibold text-[var(--text-primary)]">{lead.score}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[13px] text-[var(--text-tertiary)] py-4 text-center">
              Keine Leads mit Score &gt; 70
            </div>
          )}
          <button
            onClick={() => navigate('/sales/anfragen')}
            className="flex items-center gap-1 mt-3 text-[12px] font-medium text-[var(--primary)] hover:text-[var(--primary-hover)]"
          >
            {t('common.showAll') || 'Alle anzeigen'} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Onboarding Status */}
        <div className="border border-[var(--border-card)] rounded-2xl bg-[var(--bg-card)] p-5">
          <h3 className="text-[14px] font-semibold text-[var(--text-primary)] mb-3">
            {t('sales.dashboard.onboardingStatus') || 'Onboarding'}
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <span className="text-[13px] text-[var(--text-secondary)]">
                {t('sales.onboarding.active') || 'Aktiv'}
              </span>
              <span className="text-[15px] font-semibold text-[var(--text-primary)]">
                {parseInt(kpis.onboarding?.aktiv) || 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-t border-[var(--border-table)]">
              <span className="text-[13px] text-[var(--text-secondary)]">
                {t('sales.onboarding.completed') || 'Abgeschlossen'}
              </span>
              <span className="text-[15px] font-semibold text-[var(--text-primary)]">
                {parseInt(kpis.onboarding?.abgeschlossen) || 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-t border-[var(--border-table)]">
              <span className="text-[13px] text-[var(--text-secondary)]">{t('sales.onboarding.goLiveThisWeek')}</span>
              <span className="text-[15px] font-semibold text-[var(--text-primary)]">
                {parseInt(kpis.onboarding?.go_live_diese_woche) || 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-t border-[var(--border-table)]">
              <span className="text-[13px] text-[var(--text-secondary)]">
                {t('sales.onboarding.overdue') || 'Überfällige Aufgaben'}
              </span>
              <span className="text-[15px] font-semibold text-[var(--text-primary)]">
                {parseInt(kpis.onboarding?.ueberfaellige_aufgaben) || 0}
              </span>
            </div>
          </div>
          <button
            onClick={() => navigate('/sales/onboarding')}
            className="flex items-center gap-1 mt-3 text-[12px] font-medium text-[var(--primary)] hover:text-[var(--primary-hover)]"
          >
            {t('sales.onboarding.goTo') || 'Zu Onboarding'} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
